import React, { useState, useEffect, useCallback } from 'react';
import { 
  MessageSquare, CreditCard, Send, Users, Clock, 
  AlertTriangle, CheckCircle, XCircle, Building2, Home,
  RefreshCw, Search, Phone, User, DollarSign, ChevronDown, ChevronUp,
  Copy, Plus
} from 'lucide-react';
import api from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Modal from '../components/Common/Modal';

// ─── Toast ───────────────────────────────────────────────────────────────────
const Toast = ({ toast, onClose }) => {
  if (!toast) return null;
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [toast, onClose]);
  return (
    <div className={`fixed top-4 right-4 z-50 flex items-center space-x-2 px-4 py-3 rounded-lg shadow-lg ${
      toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
    }`}>
      {toast.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
      <p className="text-sm font-medium">{toast.message}</p>
      <button onClick={onClose} className="ml-2 hover:opacity-80"><XCircle className="h-4 w-4" /></button>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const SMSDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [recipients, setRecipients] = useState({ tenants: [], caretakers: [], active_leases: [] });
  const [selectedLease, setSelectedLease] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('late-payments');
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [smsForm, setSmsForm] = useState({
    recipient_type: 'single',
    recipients: [],
    message: '',
    type: 'custom',
    scheduled_date: null
  });
  const [purchaseAmount, setPurchaseAmount] = useState(100);

  // ─── Late Payments state ─────────────────────────────────────────────────
  const [latePayments, setLatePayments] = useState(null);
  const [latePaymentsLoading, setLatePaymentsLoading] = useState(false);
  const [expandedAssetId, setExpandedAssetId] = useState(null);
  const [sendingSms, setSendingSms] = useState(null); // 'lease:123' | 'asset:456'

  // ─── Send by Asset state ─────────────────────────────────────────────────
  const [smsAssets, setSmsAssets] = useState([]);
  const [selectedAssetId, setSelectedAssetId] = useState('');
  const [assetUnits, setAssetUnits] = useState([]);
  const [assetUnitsLoading, setAssetUnitsLoading] = useState(false);
  const [selectedUnitPhones, setSelectedUnitPhones] = useState([]);
  const [smsBulkMessage, setSmsBulkMessage] = useState('');
  const [sendingBulkAsset, setSendingBulkAsset] = useState(false);

  const [toast, setToast] = useState(null);
  const showToast = (type, message) => setToast({ type, message });
  const closeToast = () => setToast(null);

  // ─── Fetch initial data ─────────────────────────────────────────────────
  useEffect(() => {
    fetchBalance();
    fetchMessageHistory();
    fetchTemplates();
    fetchRecipients();
  }, []);

  // Auto-select first active lease
  useEffect(() => {
    if (recipients.active_leases?.length > 0 && !selectedLease) {
      setSelectedLease(String(recipients.active_leases[0].id));
    }
  }, [recipients.active_leases]);

  const fetchBalance = async () => {
    try {
      const response = await api.get('/sms/balance');
      setBalance(response.data.data.balance);
      setTransactions(response.data.data.recent_transactions || []);
    } catch (error) {
      console.error('Error fetching balance:', error);
    }
  };

  const fetchMessageHistory = async () => {
    try {
      const response = await api.get('/sms/history?limit=50');
      setMessages(response.data.data.messages || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/sms/templates');
      setTemplates(response.data.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchRecipients = async () => {
    try {
      const response = await api.get('/sms/recipients');
      setRecipients(response.data.data);
    } catch (error) {
      console.error('Error fetching recipients:', error);
    }
  };

  // ─── Late Payments ──────────────────────────────────────────────────────
  const fetchLatePayments = useCallback(async () => {
    setLatePaymentsLoading(true);
    try {
      const res = await api.get('/sms/late-payments');
      setLatePayments(res.data.data);
    } catch (error) {
      console.error('Error fetching late payments:', error);
      showToast('error', 'Failed to load late payments');
    } finally {
      setLatePaymentsLoading(false);
    }
  }, []);

  const handleSendLateSms = async (leaseId) => {
    setSendingSms(`lease:${leaseId}`);
    try {
      await api.post(`/sms/late-payment/${leaseId}`);
      showToast('success', 'Reminder sent successfully');
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to send SMS');
    } finally {
      setSendingSms(null);
    }
  };

  const handleSendAssetLateSms = async (assetId) => {
    setSendingSms(`asset:${assetId}`);
    try {
      const res = await api.post(`/sms/send-by-asset/${assetId}`);
      const data = res.data.data;
      showToast('success', `Sent ${data.sent}/${data.total} reminders`);
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to send reminders');
    } finally {
      setSendingSms(null);
    }
  };

  // ─── Send by Asset ──────────────────────────────────────────────────────
  const fetchSmsAssets = useCallback(async () => {
    try {
      const res = await api.get('/sms/assets');
      setSmsAssets(res.data.data || []);
    } catch (error) {
      console.error('Error fetching SMS assets:', error);
    }
  }, []);

  const fetchAssetUnits = useCallback(async (assetId) => {
    if (!assetId) { setAssetUnits([]); return; }
    setAssetUnitsLoading(true);
    try {
      const res = await api.get(`/payments/overview/assets/${assetId}/units`);
      setAssetUnits(res.data.data || []);
      setSelectedUnitPhones([]);
    } catch (error) {
      console.error('Error fetching asset units:', error);
      setAssetUnits([]);
    } finally {
      setAssetUnitsLoading(false);
    }
  }, []);

  const handleSendBulkByAsset = async () => {
    if (!selectedAssetId || selectedUnitPhones.length === 0) {
      showToast('error', 'Select at least one tenant');
      return;
    }
    if (!smsBulkMessage.trim()) {
      showToast('error', 'Enter a message');
      return;
    }
    setSendingBulkAsset(true);
    try {
      const payload = {
        recipients: selectedUnitPhones,
        message: smsBulkMessage,
        type: 'custom',
        related_entity_type: 'lease',
        related_entity_id: parseInt(selectedAssetId)
      };
      const res = await api.post('/sms/send-bulk', payload);
      showToast('success', `Sent ${res.data.data.success} SMS(s)`);
      setSmsBulkMessage('');
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to send SMS');
    } finally {
      setSendingBulkAsset(false);
    }
  };

  // Switch to late-payments tab → fetch data
  useEffect(() => {
    if (activeTab === 'late-payments') fetchLatePayments();
    if (activeTab === 'send-by-asset') fetchSmsAssets();
  }, [activeTab, fetchLatePayments, fetchSmsAssets]);

  // ─── Purchase credits ───────────────────────────────────────────────────
  const handlePurchaseCredits = async () => {
    setLoading(true);
    try {
      await api.post('/sms/purchase', { amount: purchaseAmount, payment_method: 'mpesa' });
      await fetchBalance();
      setShowPurchaseModal(false);
      showToast('success', `Purchased ${purchaseAmount} SMS credits!`);
    } catch (error) {
      showToast('error', 'Failed to purchase credits');
    } finally {
      setLoading(false);
    }
  };

  // ─── Send SMS ───────────────────────────────────────────────────────────
  const handleSendSMS = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { message: smsForm.message, type: smsForm.type };
      if (selectedLease) {
        payload.related_entity_type = 'lease';
        payload.related_entity_id = parseInt(selectedLease);
      }
      if (smsForm.recipient_type === 'single' && smsForm.recipients.length === 1) {
        payload.recipient = smsForm.recipients[0];
        await api.post('/sms/send', payload);
      } else if (smsForm.recipient_type === 'bulk') {
        payload.recipients = smsForm.recipients;
        await api.post('/sms/send-bulk', payload);
      }
      showToast('success', 'SMS sent successfully!');
      setSmsForm({ recipient_type: 'single', recipients: [], message: '', type: 'custom', scheduled_date: null });
      setSelectedLease('');
      fetchBalance();
      fetchMessageHistory();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to send SMS');
    } finally {
      setLoading(false);
    }
  };

  const applyTemplate = (template) => {
    setSmsForm({ ...smsForm, message: template.template });
    setSelectedTemplate(template);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'delivered': return <CheckCircle className="h-4 w-4 text-blue-500" />;
      case 'failed': return <XCircle className="h-4 w-4 text-red-500" />;
      default: return <Clock className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getTypeBadge = (type) => {
    const types = {
      late_payment: 'bg-red-100 text-red-800',
      rent_reminder: 'bg-blue-100 text-blue-800',
      maintenance_assigned: 'bg-yellow-100 text-yellow-800',
      maintenance_resolved: 'bg-green-100 text-green-800',
      lease_expiring: 'bg-orange-100 text-orange-800',
      custom: 'bg-gray-100 text-gray-800'
    };
    return types[type] || types.custom;
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return 'KES 0';
    return `KES ${parseFloat(amount).toLocaleString()}`;
  };

  return (
    <div className="space-y-6">
      <Toast toast={toast} onClose={closeToast} />

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <MessageSquare className="h-8 w-8 text-indigo-600 mr-3" />
            SMS Communications
          </h1>
          <p className="text-gray-600 mt-1">Send reminders, custom messages, and manage credits</p>
        </div>
        <button
          onClick={() => setShowPurchaseModal(true)}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          <CreditCard className="h-5 w-5 mr-2" />
          Purchase Credits
        </button>
      </div>

      {/* Balance Card */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 text-white">
          <p className="text-sm opacity-90">Available Credits</p>
          <p className="text-3xl font-bold">{balance}</p>
          <p className="text-xs opacity-75 mt-1">1 credit = 1 SMS</p>
        </div>
        <div className="bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg p-6 text-white">
          <p className="text-sm opacity-90">Cost per SMS</p>
          <p className="text-3xl font-bold">KES 1.50</p>
          <p className="text-xs opacity-75 mt-1">Including tax</p>
        </div>
        <div className="bg-gradient-to-r from-green-500 to-teal-600 rounded-lg p-6 text-white">
          <p className="text-sm opacity-90">Total Sent</p>
          <p className="text-3xl font-bold">{messages.length}</p>
          <p className="text-xs opacity-75 mt-1">Messages</p>
        </div>
        <div className={`bg-gradient-to-r rounded-lg p-6 text-white ${
          latePayments?.total_overdue_units > 0 ? 'from-red-500 to-orange-600' : 'from-gray-500 to-gray-600'
        }`}>
          <p className="text-sm opacity-90">Late Payments</p>
          <p className="text-3xl font-bold">{latePayments?.total_overdue_units || 0}</p>
          <p className="text-xs opacity-75 mt-1">
            Arrears: {formatCurrency(latePayments?.total_arrears || 0)}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {[
            { id: 'late-payments', label: 'Late Payments', icon: AlertTriangle },
            { id: 'send-by-asset', label: 'Send by Asset', icon: Building2 },
            { id: 'send', label: 'Send SMS', icon: Send },
            { id: 'history', label: 'Message History', icon: Clock },
            { id: 'templates', label: 'Templates', icon: Copy },
            { id: 'transactions', label: 'Transactions', icon: CreditCard }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 inline-flex items-center border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-5 w-5 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* ═══════════════════ TAB: LATE PAYMENTS ═══════════════════ */}
      {activeTab === 'late-payments' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Overdue Tenants</h2>
            <button onClick={fetchLatePayments} className="flex items-center px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm">
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </button>
          </div>

          {latePaymentsLoading ? (
            <LoadingSpinner size="lg" />
          ) : !latePayments || latePayments.assets?.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-400" />
              <p className="text-gray-500 font-medium">No overdue payments</p>
              <p className="text-gray-400 text-sm">All tenants are up to date!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {latePayments.assets.map(asset => {
                const isExpanded = expandedAssetId === asset.asset_id;
                const isSendingAsset = sendingSms === `asset:${asset.asset_id}`;

                return (
                  <div key={asset.asset_id} className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
                    {/* Asset header */}
                    <button
                      onClick={() => setExpandedAssetId(isExpanded ? null : asset.asset_id)}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center justify-between"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="h-9 w-9 rounded-full bg-red-100 flex items-center justify-center">
                          <AlertTriangle className="h-5 w-5 text-red-600" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900">{asset.asset_name}</h3>
                          <p className="text-xs text-gray-500">
                            {asset.overdue_count} unit{asset.overdue_count > 1 ? 's' : ''} overdue &middot; {formatCurrency(asset.total_arrears)} arrears
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleSendAssetLateSms(asset.asset_id); }}
                          disabled={isSendingAsset}
                          className="flex items-center px-3 py-1.5 text-xs font-medium bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
                        >
                          <Send className="h-3.5 w-3.5 mr-1" />
                          {isSendingAsset ? 'Sending...' : `Send All (${asset.overdue_count})`}
                        </button>
                        {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                      </div>
                    </button>

                    {/* Expanded units */}
                    {isExpanded && (
                      <div className="border-t divide-y divide-gray-100">
                        {asset.units.map(unit => {
                          const isSending = sendingSms === `lease:${unit.lease_id}`;
                          return (
                            <div key={unit.unit_id} className="px-4 py-3 flex items-center justify-between bg-red-50/50">
                              <div className="flex items-start space-x-3 min-w-0">
                                <div className="mt-0.5">
                                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-red-500" />
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center space-x-2 flex-wrap">
                                    <span className="text-sm font-medium text-gray-900">
                                      {unit.unit_number || unit.unit_name || `Unit #${unit.unit_id}`}
                                    </span>
                                    <span className="text-xs font-bold bg-red-200 text-red-800 px-2 py-0.5 rounded-full">
                                      {unit.months_overdue} mo overdue
                                    </span>
                                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                                      {formatCurrency(unit.total_arrears)}
                                    </span>
                                  </div>
                                  {unit.tenant && (
                                    <div className="flex items-center space-x-2 mt-0.5 text-xs text-gray-600">
                                      <User className="h-3 w-3" />
                                      <span>{unit.tenant.first_name} {unit.tenant.last_name}</span>
                                      {unit.tenant.phone && (
                                        <><span>&middot;</span><Phone className="h-3 w-3" /><span>{unit.tenant.phone}</span></>
                                      )}
                                    </div>
                                  )}
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    {formatCurrency(unit.rent_amount)}/mo &middot; Due day: {unit.payment_due_day}
                                    {unit.current_month_paid && <span className="text-green-600 ml-2">✓ This month paid</span>}
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => handleSendLateSms(unit.lease_id)}
                                disabled={isSending}
                                className="flex items-center px-3 py-1.5 text-xs font-medium bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex-shrink-0 ml-2"
                              >
                                <Send className="h-3.5 w-3.5 mr-1" />
                                {isSending ? '...' : 'Send'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════ TAB: SEND BY ASSET ═══════════════════ */}
      {activeTab === 'send-by-asset' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Building2 className="h-5 w-5 mr-2 text-indigo-600" />
            Send Custom SMS to Tenants in an Asset
          </h2>

          {/* Asset selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Asset</label>
            <select
              value={selectedAssetId}
              onChange={(e) => { setSelectedAssetId(e.target.value); fetchAssetUnits(e.target.value); }}
              className="w-full rounded-lg border border-gray-300 p-2"
            >
              <option value="">Choose an asset...</option>
              {smsAssets.map(a => (
                <option key={a.id} value={a.id}>
                  {a.name} — {a.leased_units} leased / {a.total_units} units
                </option>
              ))}
            </select>
          </div>

          {selectedAssetId && (
            <>
              {assetUnitsLoading ? (
                <LoadingSpinner size="sm" />
              ) : (
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700">Tenants</p>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => {
                          const allPhones = assetUnits
                            .filter(u => u.tenant?.phone)
                            .map(u => u.tenant.phone);
                          setSelectedUnitPhones(allPhones);
                        }}
                        className="px-2 py-1 text-xs bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedUnitPhones([])}
                        className="px-2 py-1 text-xs bg-gray-50 text-gray-600 rounded hover:bg-gray-100"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="border rounded-lg max-h-60 overflow-y-auto divide-y divide-gray-100">
                    {assetUnits.length === 0 ? (
                      <p className="text-sm text-gray-400 text-center py-4">No units found</p>
                    ) : (
                      assetUnits.map(unit => (
                        <label key={unit.unit_id} className="flex items-center p-2 hover:bg-gray-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedUnitPhones.includes(unit.tenant?.phone)}
                            disabled={!unit.tenant?.phone}
                            onChange={(e) => {
                              if (!unit.tenant?.phone) return;
                              if (e.target.checked) {
                                setSelectedUnitPhones([...selectedUnitPhones, unit.tenant.phone]);
                              } else {
                                setSelectedUnitPhones(selectedUnitPhones.filter(p => p !== unit.tenant.phone));
                              }
                            }}
                            className="mr-3"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-1">
                              <span className="text-sm font-medium">
                                {unit.unit_number || unit.unit_name || `Unit #${unit.unit_id}`}
                              </span>
                              {unit.tenant ? (
                                <span className="text-sm text-gray-600 ml-1">
                                  — {unit.tenant.first_name} {unit.tenant.last_name}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400 ml-1">(Vacant)</span>
                              )}
                            </div>
                            {unit.tenant?.phone && (
                              <p className="text-xs text-gray-500">{unit.tenant.phone}</p>
                            )}
                          </div>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                            unit.payment_status === 'paid' ? 'bg-green-50 text-green-700' :
                            unit.payment_status === 'overdue' ? 'bg-red-50 text-red-700' :
                            unit.payment_status === 'unpaid' ? 'bg-orange-50 text-orange-700' :
                            'bg-gray-50 text-gray-500'
                          }`}>
                            {unit.payment_status}
                          </span>
                        </label>
                      ))
                    )}
                  </div>
                  <p className="text-sm text-indigo-600 font-medium">
                    Selected: {selectedUnitPhones.length}
                  </p>
                </div>
              )}

              {/* Message */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  value={smsBulkMessage}
                  onChange={(e) => setSmsBulkMessage(e.target.value)}
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 p-3 focus:ring-2 focus:ring-indigo-500"
                  placeholder="Type your message here... Use {'{tenant_name}'}, {'{asset_name}'}, {'{amount}'} to auto-fill"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Credits: {Math.ceil(smsBulkMessage.length / 160)} &middot; Characters: {smsBulkMessage.length}
                </p>
              </div>

              <button
                onClick={handleSendBulkByAsset}
                disabled={sendingBulkAsset || selectedUnitPhones.length === 0 || !smsBulkMessage.trim()}
                className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center"
              >
                {sendingBulkAsset ? <LoadingSpinner size="sm" /> : <Send className="h-5 w-5 mr-2" />}
                {sendingBulkAsset ? 'Sending...' : `Send to ${selectedUnitPhones.length} tenant(s)`}
              </button>
            </>
          )}
        </div>
      )}

      {/* ═══════════════════ TAB: SEND SMS ═══════════════════ */}
      {activeTab === 'send' && (
        <div className="bg-white rounded-lg shadow p-6">
          <form onSubmit={handleSendSMS} className="space-y-6">
            {/* Recipient Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Send To</label>
              <div className="flex space-x-4 mb-4">
                <label className="flex items-center">
                  <input type="radio" value="single" checked={smsForm.recipient_type === 'single'}
                    onChange={(e) => setSmsForm({ ...smsForm, recipient_type: e.target.value, recipients: [] })} className="mr-2" />
                  Single Recipient
                </label>
                <label className="flex items-center">
                  <input type="radio" value="bulk" checked={smsForm.recipient_type === 'bulk'}
                    onChange={(e) => setSmsForm({ ...smsForm, recipient_type: e.target.value, recipients: [] })} className="mr-2" />
                  Bulk SMS
                </label>
              </div>

              {smsForm.recipient_type === 'single' ? (
                <select
                  value={smsForm.recipients[0] || ''}
                  onChange={(e) => setSmsForm({ ...smsForm, recipients: [e.target.value] })}
                  className="w-full rounded-lg border border-gray-300 p-2" required
                >
                  <option value="">Select recipient...</option>
                  <optgroup label="Tenants">
                    {recipients.tenants?.map(tenant => (
                      <option key={tenant.id} value={tenant.phone}>{tenant.first_name} {tenant.last_name} - {tenant.phone}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Caretakers">
                    {recipients.caretakers?.map(caretaker => (
                      <option key={caretaker.id} value={caretaker.phone}>{caretaker.first_name} {caretaker.last_name} - {caretaker.phone}</option>
                    ))}
                  </optgroup>
                </select>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <input type="text" placeholder="Search users..." value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)} className="flex-1 rounded-lg border border-gray-300 p-2 text-sm" />
                    <select value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)} className="rounded-lg border border-gray-300 p-2 text-sm">
                      <option value="all">All Roles</option>
                      <option value="tenant">Tenants Only</option>
                      <option value="caretaker">Caretakers Only</option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <button type="button" onClick={() => {
                      let allPhones = [];
                      if (tenantFilter === 'all' || tenantFilter === 'tenant') allPhones = allPhones.concat(recipients.tenants?.map(t => t.phone) || []);
                      if (tenantFilter === 'all' || tenantFilter === 'caretaker') allPhones = allPhones.concat(recipients.caretakers?.map(c => c.phone) || []);
                      if (searchQuery.trim()) {
                        const q = searchQuery.toLowerCase();
                        allPhones = allPhones.filter(p => {
                          const user = [...(recipients.tenants || []), ...(recipients.caretakers || [])].find(u => u.phone === p);
                          if (!user) return false;
                          return `${user.first_name} ${user.last_name} ${user.phone}`.toLowerCase().includes(q);
                        });
                      }
                      setSmsForm({ ...smsForm, recipients: allPhones });
                    }} className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100 text-xs font-medium">
                      Select All Filtered
                    </button>
                    <button type="button" onClick={() => setSmsForm({ ...smsForm, recipients: [] })} className="px-3 py-1 bg-gray-50 text-gray-600 rounded hover:bg-gray-100 text-xs font-medium">
                      Clear All
                    </button>
                  </div>
                  <div className="border rounded-lg p-3 max-h-48 overflow-y-auto">
                    {(() => {
                      const allUsers = [];
                      if (tenantFilter === 'all' || tenantFilter === 'tenant') allUsers.push(...(recipients.tenants?.map(t => ({ ...t, role: 'tenant' })) || []));
                      if (tenantFilter === 'all' || tenantFilter === 'caretaker') allUsers.push(...(recipients.caretakers?.map(c => ({ ...c, role: 'caretaker' })) || []));
                      const filtered = searchQuery.trim()
                        ? allUsers.filter(u => `${u.first_name} ${u.last_name} ${u.phone}`.toLowerCase().includes(searchQuery.toLowerCase()))
                        : allUsers;
                      return filtered.length > 0 ? filtered.map(user => (
                        <label key={user.id} className="flex items-center p-2 hover:bg-gray-50 rounded">
                          <input type="checkbox" value={user.phone} checked={smsForm.recipients.includes(user.phone)}
                            onChange={(e) => { if (e.target.checked) setSmsForm({ ...smsForm, recipients: [...smsForm.recipients, user.phone] });
                              else setSmsForm({ ...smsForm, recipients: smsForm.recipients.filter(p => p !== user.phone) }); }}
                            className="mr-3" />
                          <div className="flex-1">
                            <div className="flex items-center">
                              <p className="font-medium text-sm">{user.first_name} {user.last_name}</p>
                              <span className={`ml-2 px-1.5 py-0.5 text-xs rounded ${user.role === 'tenant' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`}>{user.role}</span>
                            </div>
                            <p className="text-xs text-gray-500">{user.phone}</p>
                          </div>
                        </label>
                      )) : <p className="text-sm text-gray-400 text-center py-4">No users found</p>;
                    })()}
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-gray-500">Showing {(() => {
                      const allUsers = [];
                      if (tenantFilter === 'all' || tenantFilter === 'tenant') allUsers.push(...(recipients.tenants || []));
                      if (tenantFilter === 'all' || tenantFilter === 'caretaker') allUsers.push(...(recipients.caretakers || []));
                      const filtered = searchQuery.trim()
                        ? allUsers.filter(u => `${u.first_name} ${u.last_name} ${u.phone}`.toLowerCase().includes(searchQuery.toLowerCase()))
                        : allUsers;
                      return filtered.length;
                    })()} users</p>
                    <p className="text-sm font-medium text-indigo-600">Selected: {smsForm.recipients.length}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Templates */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Quick Templates</label>
              <div className="flex flex-wrap gap-2">
                {templates.map(template => (
                  <button key={template.id} type="button" onClick={() => applyTemplate(template)}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200">{template.name}</button>
                ))}
              </div>
            </div>

            {/* Message Type & Lease */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Message Type</label>
              <select value={smsForm.type} onChange={(e) => setSmsForm({ ...smsForm, type: e.target.value })}
                className="w-full rounded-lg border border-gray-300 p-2">
                <option value="custom">Custom Message</option>
                <option value="late_payment">Late Payment Notice</option>
                <option value="rent_reminder">Rent Reminder</option>
                <option value="maintenance_assigned">Maintenance Assigned</option>
                <option value="maintenance_resolved">Maintenance Resolved</option>
                <option value="lease_expiring">Lease Expiring</option>
              </select>
            </div>
            {smsForm.message.includes('{') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Lease <span className="text-gray-400 font-normal">(for template variables)</span>
                </label>
                <select value={selectedLease} onChange={(e) => setSelectedLease(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 p-2">
                  <option value="">Auto-detect from first active lease</option>
                  {recipients.active_leases?.map(lease => (
                    <option key={lease.id} value={lease.id}>
                      {lease.asset_name} — {lease.first_name} {lease.last_name} (Lease #{lease.lease_number})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Select a lease to populate variables like {'{asset_name}'}, {'{amount}'}, {'{days_remaining}'}
                </p>
              </div>
            )}

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
              <textarea value={smsForm.message} onChange={(e) => setSmsForm({ ...smsForm, message: e.target.value })}
                rows={5} className="w-full rounded-lg border border-gray-300 p-3 focus:ring-2 focus:ring-indigo-500"
                placeholder="Type your message here..." required />
              <div className="flex justify-between mt-1">
                <p className="text-xs text-gray-500">Credits needed: {Math.ceil(smsForm.message.length / 160)}</p>
                <p className="text-xs text-gray-500">Characters: {smsForm.message.length} / {smsForm.message.length > 160 ? 'Multiple SMS' : '1 SMS'}</p>
              </div>
            </div>

            {/* Cost */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">Estimated Cost</p>
                  <p className="text-lg font-bold text-indigo-600">KES {(Math.ceil(smsForm.message.length / 160) * 1.5).toFixed(2)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Credits to Use</p>
                  <p className="text-lg font-bold text-green-600">{Math.ceil(smsForm.message.length / 160)}</p>
                </div>
              </div>
            </div>

            <button type="submit" disabled={loading || smsForm.recipients.length === 0 || !smsForm.message}
              className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center">
              {loading ? <LoadingSpinner size="sm" /> : <Send className="h-5 w-5 mr-2" />}
              {loading ? 'Sending...' : 'Send SMS'}
            </button>
          </form>
        </div>
      )}

      {/* ═══════════════════ TAB: MESSAGE HISTORY ═══════════════════ */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipient</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Message</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {messages.map(msg => (
                  <tr key={msg.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-500">{new Date(msg.created_at).toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{msg.recipient}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-md truncate">{msg.message?.substring(0, 100)}...</td>
                    <td className="px-6 py-4"><span className={`px-2 py-1 text-xs rounded-full capitalize ${getTypeBadge(msg.type)}`}>{msg.type?.replace('_', ' ')}</span></td>
                    <td className="px-6 py-4"><div className="flex items-center space-x-2">{getStatusIcon(msg.status)}<span className="text-sm capitalize">{msg.status}</span></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══════════════════ TAB: TEMPLATES ═══════════════════ */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={() => setShowTemplateModal(true)}
              className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
              <Plus className="h-5 w-5 mr-2" /> Create Template
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates.map(template => (
              <div key={template.id} className="bg-white rounded-lg shadow p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-semibold text-gray-900">{template.name}</h3>
                  <span className={`px-2 py-1 text-xs rounded-full capitalize ${getTypeBadge(template.type)}`}>{template.type?.replace('_', ' ')}</span>
                </div>
                <p className="text-sm text-gray-600 mb-3">{template.template}</p>
                <div className="flex space-x-2">
                  <button onClick={() => { applyTemplate(template); setActiveTab('send'); }} className="text-indigo-600 text-sm hover:text-indigo-800">Use Template</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══════════════════ TAB: TRANSACTIONS ═══════════════════ */}
      {activeTab === 'transactions' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Credits</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.map((txn, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-500">{txn.created_at ? new Date(txn.created_at).toLocaleString() : '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{formatCurrency(txn.amount)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{txn.credits || '-'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600 capitalize">{txn.payment_method || txn.method || 'N/A'}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        (txn.status === 'completed' || txn.type === 'credit') ? 'bg-green-100 text-green-800' :
                        txn.status === 'failed' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>{txn.status || txn.type}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Purchase Credits Modal */}
      <Modal isOpen={showPurchaseModal} onClose={() => setShowPurchaseModal(false)} title="Purchase SMS Credits" size="md">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount (KES)</label>
            <div className="grid grid-cols-4 gap-2 mb-2">
              {[100, 500, 1000, 5000].map(amount => (
                <button key={amount} type="button" onClick={() => setPurchaseAmount(amount)}
                  className={`p-2 border rounded-lg text-sm ${purchaseAmount === amount ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-300 hover:border-gray-400'}`}>
                  KES {amount}
                </button>
              ))}
            </div>
            <input type="number" value={purchaseAmount} onChange={(e) => setPurchaseAmount(parseInt(e.target.value))}
              className="w-full rounded-lg border border-gray-300 p-2" min="10" step="10" />
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-blue-800">
              You will receive <strong>{purchaseAmount}</strong> SMS credits<br/>
              Each credit = 1 SMS (up to 160 characters)<br/>
              Total cost: <strong>KES {purchaseAmount}</strong>
            </p>
          </div>
          <button onClick={handlePurchaseCredits} disabled={loading}
            className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700">
            {loading ? 'Processing...' : `Pay KES ${purchaseAmount}`}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default SMSDashboard;
